import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from '../styles/theme';

// Create a theme context with extended functionality
const ThemeContext = createContext();

// Define preset themes for the configurator
export const PRESET_THEMES = {
  default: {
    light: {
      bg: '#f8fafc',
      secondary: '#e2e8f0',
      text: '#0f172a',
      textSecondary: '#475569',
      accent: '#ffa600', 
      card: '#ffffff'
    },
    dark: {
      bg: '#0F172A',
      secondary: '#1E293B',
      text: '#E2E8F0',
      textSecondary: '#94a3b8',
      accent: '#ffa600',
      card: '#1E293B'
    }
  },
  ocean: {
    light: {
      bg: '#f0f9ff',
      secondary: '#e0f2fe',
      text: '#0c4a6e',
      textSecondary: '#0369a1',
      accent: '#0284c7',
      card: '#ffffff'
    },
    dark: {
      bg: '#0c4a6e',
      secondary: '#075985',
      text: '#e0f2fe',
      textSecondary: '#bae6fd',
      accent: '#0ea5e9',
      card: '#0c4a6e'
    }
  },
  forest: {
    light: {
      bg: '#f0fdf4',
      secondary: '#dcfce7',
      text: '#166534',
      textSecondary: '#15803d',
      accent: '#16a34a',
      card: '#ffffff'
    },
    dark: {
      bg: '#14532d',
      secondary: '#15803d',
      text: '#dcfce7',
      textSecondary: '#bbf7d0',
      accent: '#22c55e',
      card: '#14532d'
    }
  },
  purple: {
    light: {
      bg: '#faf5ff',
      secondary: '#f3e8ff',
      text: '#581c87',
      textSecondary: '#7e22ce',
      accent: '#a855f7',
      card: '#ffffff'
    },
    dark: {
      bg: '#4c1d95',
      secondary: '#6b21a8',
      text: '#f3e8ff',
      textSecondary: '#e9d5ff',
      accent: '#d946ef',
      card: '#4c1d95'
    }
  }
};

export const ThemeProvider = ({ children }) => {
  // Base light/dark mode state
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Current active preset
  const [activePreset, setActivePreset] = useState('default');
  
  // Custom colors state - initialize from preset
  const [customColors, setCustomColors] = useState(
    isDark ? PRESET_THEMES.default.dark : PRESET_THEMES.default.light
  );
  
  // Current applied theme - combines base theme with custom colors
  const [currentTheme, setCurrentTheme] = useState(() => {
    const baseTheme = isDark ? darkTheme : lightTheme;
    // Start with default preset
    return {
      ...baseTheme,
      isDark: isDark, // Make sure isDark is included
      colors: {
        ...baseTheme.colors,
        ...(isDark ? PRESET_THEMES.default.dark : PRESET_THEMES.default.light)
      }
    };
  });

  // Toggle between light and dark mode
  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };
  
  // Apply a preset theme
  const applyPreset = (preset) => {
    if (!PRESET_THEMES[preset]) return;
    
    setActivePreset(preset);
    const presetColors = isDark ? PRESET_THEMES[preset].dark : PRESET_THEMES[preset].light;
    setCustomColors(presetColors);
    
    // Update the current theme with preset colors
    setCurrentTheme(prevTheme => ({
      ...prevTheme,
      isDark: isDark, // Make sure isDark is included
      colors: {
        ...prevTheme.colors,
        ...presetColors
      }
    }));
  };
  
  // Apply custom colors
  const applyCustomColors = (colors) => {
    setCustomColors(colors);
    
    // Update the current theme with custom colors
    setCurrentTheme(prevTheme => ({
      ...prevTheme,
      isDark: isDark, // Make sure isDark is included
      colors: {
        ...prevTheme.colors,
        ...colors
      }
    }));
  };
  
  // Update a single color
  const updateColor = (colorKey, value) => {
    setCustomColors(prev => ({
      ...prev,
      [colorKey]: value
    }));
    
    // Update the current theme with the changed color
    setCurrentTheme(prevTheme => ({
      ...prevTheme,
      isDark: isDark, // Make sure isDark is included
      colors: {
        ...prevTheme.colors,
        [colorKey]: value
      }
    }));
  };
  
  // Reset to default theme
  const resetTheme = () => {
    setActivePreset('default');
    const defaultColors = isDark ? PRESET_THEMES.default.dark : PRESET_THEMES.default.light;
    setCustomColors(defaultColors);
    
    // Reset to base theme with default preset
    setCurrentTheme({
      ...(isDark ? darkTheme : lightTheme),
      isDark: isDark, // Make sure isDark is included
      colors: {
        ...(isDark ? darkTheme : lightTheme).colors,
        ...defaultColors
      }
    });
  };

  // Update theme when light/dark mode changes
  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    
    // When toggling light/dark, keep the current preset or custom colors
    const baseTheme = isDark ? darkTheme : lightTheme;
    const themeColors = isDark ? PRESET_THEMES[activePreset].dark : PRESET_THEMES[activePreset].light;
    
    setCustomColors(themeColors);
    
    // Update current theme with base theme and preset colors
    setCurrentTheme({
      ...baseTheme,
      isDark: isDark, // Make sure isDark is included
      colors: {
        ...baseTheme.colors,
        ...themeColors
      }
    });
  }, [isDark, activePreset]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    isDark, 
    toggleTheme,
    activePreset,
    customColors,
    applyPreset,
    applyCustomColors,
    updateColor,
    resetTheme
  }), [isDark, activePreset, customColors]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledThemeProvider theme={currentTheme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};